import React, { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { REACTIVATION_ESTIMATE_MS } from '../hooks/useAgentReactivation';

const TICK_MS = 500;

/**
 * Never reaches 100% on its own. Setup finishes when the JOB finishes, and a full bar that then
 * keeps spinning reads as a stall — so the estimate asymptotes just short and the bar disappears
 * on the real completion instead.
 */
const MAX_ESTIMATED_PERCENT = 95;

/**
 * Progress of a running reactivation against {@link REACTIVATION_ESTIMATE_MS}.
 *
 * Purely an estimate — agent setup waits on HyperEVM inclusion and HyperCore confirmation, which
 * arrive on their own schedule — so `overrun` reports when it has run past the estimate. The bar pulses at that point
 * rather than claiming a percentage it cannot know.
 */
export function reactivationProgress(
    startedAtMs: number | null,
    nowMs: number,
    estimateMs: number = REACTIVATION_ESTIMATE_MS,
): { percent: number; secondsElapsed: number; overrun: boolean } {
    if (startedAtMs === null || !Number.isFinite(startedAtMs)) {
        return { percent: 0, secondsElapsed: 0, overrun: false };
    }
    // a clock skew or a future-dated job must not produce a negative bar
    const elapsed = Math.max(0, nowMs - startedAtMs);
    return {
        percent: Math.min(MAX_ESTIMATED_PERCENT, (elapsed / estimateMs) * 100),
        secondsElapsed: Math.floor(elapsed / 1000),
        overrun: elapsed > estimateMs,
    };
}

interface ReactivationProgressProps {
    /** Epoch ms the reactivation started; null renders an empty bar rather than nothing. */
    startedAtMs: number | null;
    className?: string;
}

const ReactivationProgress: React.FC<ReactivationProgressProps> = ({
    startedAtMs,
    className,
}) => {
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        if (startedAtMs === null) return;
        const timer = setInterval(() => setNowMs(Date.now()), TICK_MS);
        return () => clearInterval(timer);
    }, [startedAtMs]);

    const { percent, overrun } = reactivationProgress(startedAtMs, nowMs);

    return (
        <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(percent)}
            aria-label="Snappy Wallet setup progress"
            className={cn(
                'mt-2 h-1 w-full overflow-hidden rounded-full bg-amber-500/15',
                className,
            )}
        >
            <div
                className={cn(
                    'h-full rounded-full bg-amber-400/70 transition-[width] duration-500 ease-linear',
                    // past the estimate the bar cannot advance further, so it pulses instead — the
                    // movement is what shows it is still working, with the copy gone.
                    overrun && 'animate-pulse',
                )}
                style={{ width: `${percent}%` }}
            />
        </div>
    );
};

ReactivationProgress.displayName = 'ReactivationProgress';

export default ReactivationProgress;
